import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  T, serif, fmt, Card, Boton, Campo, estiloInput,
  Titulo, Aviso, Vacio,
} from '../lib/ui';

// ════════════════════════════════════════════════════════════════════
// Enviar a SAP por DTW
//
// Dos pasos, y el primero no es un trámite: un lote de DTW se sube de
// una vez y no se deshace fácil. Ver antes qué entra, qué queda fuera y
// por qué es mucho más barato que corregir en SAP después.
//
// Los archivos se descargan desde el navegador, sin pasar por el disco
// del servidor: en Hostinger cualquier cosa escrita en la carpeta de la
// aplicación se la lleva el siguiente despliegue.
// ════════════════════════════════════════════════════════════════════

/** AAAA-MM-01 y el último día del mes en curso. */
function mesEnCurso() {
  const h = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const ultimo = new Date(h.getFullYear(), h.getMonth() + 1, 0).getDate();
  return {
    desde: `${h.getFullYear()}-${p(h.getMonth() + 1)}-01`,
    hasta: `${h.getFullYear()}-${p(h.getMonth() + 1)}-${p(ultimo)}`,
  };
}

/** Descarga un texto como archivo, sin tocar el servidor. */
function descargar(nombre, contenido) {
  // BOM + CRLF: así Excel y DTW lo abren con los acentos bien en Windows.
  const blob = new Blob(['﻿' + contenido], {
    type: 'text/plain;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Liberar después del clic, no antes: Safari cancela la descarga si
  // la dirección deja de existir mientras todavía la está leyendo.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function Dtw({ usuario }) {
  const [rango, setRango] = useState(mesEnCurso);
  const [incluirContabilizados, setIncluir] = useState(false);
  const [previo, setPrevio] = useState(null);
  const [lote, setLote] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  const filtros = { ...rango, incluirContabilizados: incluirContabilizados ? '1' : '' };

  const verPrevio = async () => {
    setOcupado(true); setError(''); setLote(null);
    try {
      setPrevio(await api.dtwPrevio(filtros));
    } catch (e) {
      setError(e.detalle?.detalle || 'No se pudo calcular el previo.');
    } finally { setOcupado(false); }
  };

  useEffect(() => { verPrevio(); }, []);   // eslint-disable-line

  const generar = async () => {
    setOcupado(true); setError('');
    try {
      const r = await api.dtwGenerar({ ...rango, incluirContabilizados });
      setLote(r);
      // Se descargan solos, en el orden en que hay que subirlos.
      const orden = [
        'OPCH - Documents.txt', 'PCH1 - Document_Lines.txt',
        'OJDT - JournalEntries.txt', 'JDT1 - JournalEntries_Lines.txt',
      ];
      orden.filter((n) => r.archivos[n])
        .forEach((n, i) => setTimeout(() => descargar(n, r.archivos[n]), i * 400));
      setPrevio(null);
    } catch (e) {
      setError(e.detalle?.detalle || 'No se pudo generar el lote.');
    } finally { setOcupado(false); }
  };

  if (!['contabilidad', 'director'].includes(usuario.rol)) {
    return <Vacio>Los archivos para SAP los genera contabilidad.</Vacio>;
  }

  return (
    <>
      <Titulo sub="Genera los archivos y súbelos a SAP con Data Transfer Workbench">
        Enviar a SAP
      </Titulo>

      {error && <Aviso tipo="bad">{error}</Aviso>}

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
          <Campo etiqueta="Desde">
            <input type="date" value={rango.desde}
                   onChange={(e) => setRango({ ...rango, desde: e.target.value })}
                   style={estiloInput} />
          </Campo>
          <Campo etiqueta="Hasta">
            <input type="date" value={rango.hasta}
                   onChange={(e) => setRango({ ...rango, hasta: e.target.value })}
                   style={estiloInput} />
          </Campo>
        </div>

        <label style={{
          display: 'flex', gap: 9, alignItems: 'flex-start',
          fontSize: 12.5, color: T.muted, marginBottom: 12, cursor: 'pointer',
        }}>
          <input type="checkbox" checked={incluirContabilizados}
                 onChange={(e) => setIncluir(e.target.checked)}
                 style={{ marginTop: 2 }} />
          <span>
            Incluir los ya contabilizados.
            <strong style={{ color: T.bad }}> Cuidado:</strong> si ya los subiste
            a SAP, volverían a entrar duplicados. Solo para rehacer un lote que
            se perdió.
          </span>
        </label>

        <Boton variante="suave" onClick={verPrevio} disabled={ocupado}
               style={{ width: '100%' }}>
          {ocupado ? 'Calculando…' : 'Ver qué saldría'}
        </Boton>
      </Card>

      {previo && <Previo p={previo} onGenerar={generar} ocupado={ocupado} />}
      {lote && <Resultado r={lote} onDescargar={descargar} />}
    </>
  );
}

// ── Qué saldría ─────────────────────────────────────────────────────
function Previo({ p, onGenerar, ocupado }) {
  const hay = p.facturas > 0 || p.asientos > 0;

  return (
    <>
      {(p.avisos || []).map((a, i) => (
        <Aviso key={i} tipo="warn">{a}</Aviso>
      ))}

      <Card style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 10, color: T.muted, fontWeight: 700,
          letterSpacing: '.05em', marginBottom: 10,
        }}>
          QUÉ SALDRÍA
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Recuadro titulo="Facturas de compra" n={p.facturas}
                    detalle={`${p.lineasFactura} línea(s) · OPCH + PCH1`} />
          <Recuadro titulo="Asientos directos" n={p.asientos}
                    detalle={`${p.lineasAsiento} línea(s) · OJDT + JDT1`} />
        </div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 10, lineHeight: 1.5 }}>
          Las facturas y boletas de proveedor van como factura de compra. La
          movilidad, los recibos internos y los sustentos sin comprobante van
          como asiento directo, porque no hay comprobante de un tercero.
        </div>
      </Card>

      {/* Lo que queda fuera. Es la parte que de verdad hay que mirar:
          un lote que calla lo que dejó afuera se sube creyendo que
          está completo. */}
      {p.rechazados?.length > 0 && (
        <Card style={{ marginBottom: 14, borderColor: T.bad }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: T.bad, marginBottom: 4,
          }}>
            {p.rechazados.length} gasto(s) NO pueden exportarse
          </div>
          <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 11 }}>
            Estos no van a entrar a SAP. Arréglalos y vuelve a calcular, o
            genera el lote sabiendo que quedan pendientes.
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {p.rechazados.map((r) => (
              <div key={r.id} style={{
                borderLeft: `3px solid ${T.bad}`, paddingLeft: 10,
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {r.id} · {fmt.money(r.total, 'PEN')}
                  <span style={{ fontWeight: 400, color: T.muted }}>
                    {' '}· {r.destino}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  {r.concepto || 'sin concepto'} · {fmt.fecha(r.fecha)}
                </div>
                {r.porque.map((x, i) => (
                  <div key={i} style={{ fontSize: 11.5, color: T.bad, marginTop: 2 }}>
                    — {x}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* A quién le debemos. La pregunta que no se podía contestar
          cuando todo se asentaba contra entregas a rendir. */}
      {p.reembolsos?.length > 0 && (
        <Card style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: T.navy, marginBottom: 4,
          }}>
            Reembolsos que salen en este lote
          </div>
          <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 11 }}>
            Gente que puso de su bolsillo. El asiento los acredita a una cuenta
            por pagar, no a entregas a rendir.
          </div>
          <div style={{ display: 'grid', gap: 7 }}>
            {p.reembolsos.map((r, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', gap: 10,
                fontSize: 12.5,
              }}>
                <span style={{ color: T.navy, fontWeight: 600 }}>{r.a}</span>
                <span style={{ fontWeight: 700 }}>{fmt.money(r.soles, 'PEN')}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Boton variante="acento" onClick={onGenerar}
             disabled={ocupado || !hay}
             style={{ width: '100%', padding: 14, fontSize: 15 }}>
        {ocupado ? 'Generando…'
          : hay ? 'Generar y descargar los archivos'
            : 'No hay nada que exportar en este periodo'}
      </Boton>
    </>
  );
}

function Recuadro({ titulo, n, detalle }) {
  return (
    <div style={{ background: T.warm, borderRadius: 8, padding: '11px 13px' }}>
      <div style={{ fontSize: 11, color: T.muted }}>{titulo}</div>
      <div style={{ fontFamily: serif, fontSize: 24, fontWeight: 700, color: T.navy }}>
        {n}
      </div>
      <div style={{ fontSize: 10.5, color: T.muted }}>{detalle}</div>
    </div>
  );
}

// ── Lote generado ───────────────────────────────────────────────────
function Resultado({ r, onDescargar }) {
  const nombres = Object.keys(r.archivos || {});

  return (
    <>
      <Aviso tipo="ok">
        Lote generado: {r.facturas} factura(s) y {r.asientos} asiento(s).
        Los archivos se están descargando.
      </Aviso>

      {r.aviso && <Aviso tipo="warn">{r.aviso}</Aviso>}

      <Card style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 10, color: T.muted, fontWeight: 700,
          letterSpacing: '.05em', marginBottom: 10,
        }}>
          LOS ARCHIVOS
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {nombres.map((n) => (
            <div key={n} style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 12.5, fontFamily: 'monospace', minWidth: 0 }}>
                {n}
              </span>
              <Boton variante="suave"
                     onClick={() => onDescargar(n, r.archivos[n])}
                     style={{ fontSize: 12, padding: '5px 11px', whiteSpace: 'nowrap' }}>
                Descargar
              </Boton>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div style={{
          fontSize: 10, color: T.muted, fontWeight: 700,
          letterSpacing: '.05em', marginBottom: 10,
        }}>
          CÓMO SUBIRLO
        </div>
        <ol style={{
          margin: 0, paddingLeft: 18, fontSize: 12.5,
          color: T.text, lineHeight: 1.6,
        }}>
          {(r.comoSubirlo || []).map((x, i) => <li key={i}>{x}</li>)}
        </ol>
      </Card>
    </>
  );
}
